import React from 'react'
import { Document, Image, Page, Text, View } from '@react-pdf/renderer'
import { CELL_STATES, LEGEND_ORDER, cellState, formatDate } from './matrixStatus'

// Training matrix as evidence: staff down the side, courses across, in
// words (colour only backs the words up). Landscape A4; more courses than
// fit across one page continue as further column groups, each starting on a
// new page with the staff names repeated.
//
// KPIReportPDF's ReportChrome is portrait-only, so the same header/footer
// look is rebuilt here for landscape pages.

const COURSES_PER_PAGE = 10
const NAME_WIDTH = 120
const ROLE_WIDTH = 82

const chunk = (list, size) => {
  const groups = []
  for (let index = 0; index < list.length; index += size) groups.push(list.slice(index, index + size))
  return groups
}

const cellText = (cell) => {
  const state = cellState(cell)
  if (state.key === 'expiring' && cell?.expiresAt) return `EXP ${formatDate(cell.expiresAt, 'DD/MM/YY')}`
  return state.pdf
}

const border = '#CBD5E1'

const MatrixPDF = ({ orgSettings, courses = [], employees = [], summary, filterLabel, printedBy }) => {
  const groups = chunk(courses, COURSES_PER_PAGE)
  const footer = `${orgSettings?.orgName ?? ''} - Training matrix`

  return (
    <Document options={{ isEvalSupported: false }} title="Training matrix">
      {groups.map((group, groupIndex) => (
        <Page
          key={group[0]._id}
          size="A4"
          orientation="landscape"
          style={{ paddingTop: 30, paddingBottom: 44, paddingHorizontal: 30, fontFamily: 'Helvetica' }}
        >
          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}
            fixed
          >
            {orgSettings?.logo ? <Image src={orgSettings.logo} style={{ height: 24 }} /> : <Text />}
            <Text style={{ fontSize: 10, color: 'grey' }}>{orgSettings?.orgName}</Text>
          </View>

          {groupIndex === 0 ? (
            <View style={{ marginBottom: 8, gap: 3 }}>
              <Text style={{ fontSize: 16, color: '#3c4e50' }}>Training matrix</Text>
              <Text style={{ fontSize: 8, color: '#64748b' }}>{printedBy}</Text>
              {filterLabel ? <Text style={{ fontSize: 8, color: '#64748b' }}>Showing: {filterLabel}</Text> : null}
              {summary?.percent !== null && summary?.percent !== undefined ? (
                <Text style={{ fontSize: 9, color: '#242F40' }}>
                  {summary.percent}% of required training is in date ({summary.compliant + summary.expiring} of{' '}
                  {summary.required}). Overdue: {summary.overdue}. Not completed: {summary.notCompleted}. Expiring
                  soon: {summary.expiring}.
                </Text>
              ) : null}
            </View>
          ) : null}

          {groups.length > 1 ? (
            <Text style={{ fontSize: 8, color: '#64748b', marginBottom: 4 }}>
              Courses {groupIndex * COURSES_PER_PAGE + 1} to {groupIndex * COURSES_PER_PAGE + group.length} of{' '}
              {courses.length}
            </Text>
          ) : null}

          <View
            style={{ flexDirection: 'row', backgroundColor: '#242F40', alignItems: 'stretch' }}
            fixed
          >
            <Text style={{ width: NAME_WIDTH, fontSize: 7, color: '#FFFFFF', padding: 4 }}>Name</Text>
            <Text style={{ width: ROLE_WIDTH, fontSize: 7, color: '#FFFFFF', padding: 4 }}>Role</Text>
            {group.map((course) => (
              <Text
                key={course._id}
                style={{ flex: 1, fontSize: 6.5, color: '#FFFFFF', padding: 3, textAlign: 'center' }}
              >
                {course.title}
              </Text>
            ))}
          </View>

          {employees.map((employee, index) => (
            <View
              key={employee._id}
              wrap={false}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: index % 2 ? '#F8FAFC' : '#FFFFFF',
                borderBottomWidth: 0.5,
                borderBottomColor: border,
              }}
            >
              <Text style={{ width: NAME_WIDTH, fontSize: 7, padding: 4 }}>
                {employee.firstName} {employee.lastName}
              </Text>
              <Text style={{ width: ROLE_WIDTH, fontSize: 6.5, padding: 4, color: '#475569' }}>
                {employee.clinicalRole}
              </Text>
              {group.map((course) => {
                const cell = employee.cells?.[course._id]
                const state = cellState(cell)
                const strong = state.key === 'overdue' || state.key === 'notCompleted'
                return (
                  <Text
                    key={course._id}
                    style={{
                      flex: 1,
                      fontSize: 6.5,
                      padding: 3,
                      textAlign: 'center',
                      fontFamily: strong ? 'Helvetica-Bold' : 'Helvetica',
                      color: strong ? '#B91C1C' : state.key === 'notRequired' || state.key === 'held' ? '#64748B' : '#242F40',
                    }}
                  >
                    {cellText(cell)}
                    {cell?.source === 'elearning' ? ' (E)' : ''}
                  </Text>
                )
              })}
            </View>
          ))}

          <View style={{ marginTop: 10 }} wrap={false}>
            <Text style={{ fontSize: 7, color: '#475569' }}>
              Key:{' '}
              {LEGEND_ORDER.map((key) => `${CELL_STATES[key].pdf} = ${CELL_STATES[key].label}`).join('   ')}
              {'   '}EXP is followed by the expiry date.{'   '}(E) = completed by e-learning.
            </Text>
          </View>

          <Text
            style={{
              position: 'absolute',
              bottom: 20,
              left: 30,
              right: 30,
              fontSize: 7,
              color: '#94a3b8',
              textAlign: 'center',
            }}
            render={({ pageNumber, totalPages }) => `${footer} - page ${pageNumber} of ${totalPages}`}
            fixed
          />
        </Page>
      ))}
    </Document>
  )
}

export default MatrixPDF
